import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import { deleteModule, duplicateModule, getModule, setSelectedModule } from '../../../state/module/module.actions';
import { openDashboardConfirmationDialog } from '../../../state/dashboard.actions';
import { getModuleItemsConfig } from '../../../state/module-item/module-item.actions';
import {
  getCalculatedVariablesForStudy
} from '../../../state/calculated-variable/calculated-variable.actions';
import {
  selectIsLoadingModules,
  selectModules,
  selectSelectedModule
} from 'src/app/dashboard/state/module/module.selectors';
import { selectSelectedLanguage, selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import { Study } from 'src/app/models/study';
import { Module } from 'src/app/models/module';
import { Language } from 'src/app/models/language';
import { STUDIES_ROUTES } from '../../studies.routes';
import { LANGUAGES, PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../core/dashboard-constants';
import { DASHBOARD_ROUTES } from '../../../dashboard.routes';
import { PrivilegeService } from '../../../services/privilege/privilege.service';
import { StudyType } from '../../../../models/study-type';
import { setSelectedLanguage } from '../../../state/study/study.actions';
import { getLanguageByCode } from '../../../../core/languages-helper';
import { sortByDate } from '../../helpers/date-helper';

const MODULES_TAB_LABELS = {
  GENERAL: 'GENERAL',
  CONTENT_ITEMS: 'CONTENT ITEMS'
};

interface SortOption {
  value: string,
  viewValue: string
}

@Component({
  selector: 'dashboard-modules',
  templateUrl: './modules.component.html',
  styleUrls: ['./modules.component.scss']
})
export class ModulesComponent implements OnInit, OnDestroy {
  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  selectedModule$: Observable<Module>;
  selectedModule: Module;

  selectedLanguage$: Observable<Language>;
  selectedLanguage: Language;

  loadingModules$: Observable<boolean>;
  modules$: Observable<Module[]>;
  allModules: Module[];
  sortedModules: Module[];

  sortOptions: SortOption[] = [
    { value: 'showAll', viewValue: 'All modules' },
    { value: 'showAtoZ', viewValue: 'A to Z' },
    { value: 'showZtoA', viewValue: 'Z to A' },
    { value: 'latest', viewValue: 'Latest created' }
  ];

  searchModules: string;
  selectedOption: string;

  isEdit = false;
  selectedTabIndex = 0;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  MODULES_TAB_LABELS = MODULES_TAB_LABELS;

  StudyType = StudyType;

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, public privilegeService: PrivilegeService) {
  }

  ngOnInit(): void {
    this.loadingModules$ = this.store.select(selectIsLoadingModules);

    this.modules$ = this.store.select(selectModules);
    this.subscriptions.add(this.modules$.subscribe((modules) => {
      this.allModules = modules;
      this.sortedModules = modules;
    }));

    this.selectedModule$ = this.store.select(selectSelectedModule);
    this.subscriptions.add(
      this.selectedModule$.subscribe((selectedModule) => {
        this.selectedModule = selectedModule;
        this.selectedModule ? (this.isEdit = true) : (this.isEdit = false);
      })
    );

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.subscriptions.add(this.selectedStudy$.subscribe((selectedStudy) => (this.selectedStudy = selectedStudy)));

    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);
    this.subscriptions.add(this.selectedLanguage$.subscribe((language) => (this.selectedLanguage = language)));
  }

  createModule(): void {
    this.isEdit = false;
    this.store.dispatch(setSelectedModule({ selectedModule: null }));
  }

  openDeleteDialog(module: Module): void {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: `Are you sure you want to delete this module?`,
          onConfirm: deleteModule({ module })
        }
      })
    );
  }

  duplicateModule(module: Module): void {
    this.store.dispatch(duplicateModule({ module }));
  }

  openModuleDetails(module: Module): void {
    this.isEdit = true;
    this.store.dispatch(getModule({ studyId: module?.studyId, moduleId: module?.id }));
    this.store.dispatch(getCalculatedVariablesForStudy({ studyId: module?.studyId }));
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: getLanguageByCode(this.selectedStudy?.languages[0]) }));
    this.selectedTabIndex = 0;
  }

  getInterventionsUrl(): string {
    return `/${DASHBOARD_ROUTES.STUDIES}/${this.selectedStudy?.id}/${STUDIES_ROUTES.INTERVENTIONS}`;
  }

  onSelectedTabChange(tabChangeEvent: MatTabChangeEvent): void {
    if (tabChangeEvent?.tab?.textLabel === MODULES_TAB_LABELS.CONTENT_ITEMS) {
      this.store.dispatch(getModuleItemsConfig());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  sort(value: string): void {
    this.selectedOption = value;
    switch (value) {
      case 'showAll':
        this.showAllModules();
        break;
      case 'showAtoZ':
        this.sortAlphabetically();
        break;
      case 'showZtoA':
        this.sortReversedAlphabetically();
        break;
      case 'latest':
        this.sortByDateCreated();
        break;
    }
  }

  filterModules(): void {
    if (this.searchModules !== '') {
      this.sort(!this.selectedOption ? 'showAll' : this.selectedOption);
      this.sortedModules = this.sortedModules.filter(module =>
        module.name?.toLowerCase().includes(this.searchModules.toLowerCase())
      );
    } else {
      this.sort(this.selectedOption);
    }
  }

  private showAllModules(): void {
    this.sortByDateCreated();
  }

  private sortAlphabetically(): void {
    this.sortedModules = this.allModules;
    this.sortedModules.sort((module1, module2) =>
      module1.name.localeCompare(module2.name)
    );
  }

  private sortReversedAlphabetically(): void {
    this.sortedModules = this.allModules;
    this.sortedModules.sort((module1, module2) =>
      module2.name.localeCompare(module1.name)
    );
  }

  private sortByDateCreated(): void {
    this.sortedModules = this.allModules;
    this.sortedModules.sort((module1, module2) =>
      sortByDate(module1.createdAt, module2.createdAt)
    );
  }
}
